import { useMemo, useState } from 'react';
import { createIndexedDBStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/indexed-db/index.tsx';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import type { InProgressIssue } from '../../common/types.tsx';
import { BROWSER_STORAGE_PREFIX } from '../constants.tsx';

export interface InProgressIssueService {
	getInProgressIssue: (inProgressIssueID: string) => Promise<InProgressIssue | undefined>;
	createInProgressIssue: (
		inProgressIssueID: string,
		data: InProgressIssue,
	) => Promise<InProgressIssue>;
	updateInProgressIssue: (
		inProgressIssueID: string,
		data: InProgressIssue,
	) => Promise<InProgressIssue>;
	deleteInProgressIssue: (inProgressIssueID: string) => Promise<void>;
}

export const useInProgressIssueService = (): InProgressIssueService => {
	const [storage] = useState(() =>
		createIndexedDBStorageProvider(`${BROWSER_STORAGE_PREFIX}in-progress-issue`, {
			shouldAutoReconnect: true,
		}),
	);

	return useMemo(
		() => ({
			getInProgressIssue: async (inProgressIssueID: string) => storage.get(inProgressIssueID),
			createInProgressIssue: async (inProgressIssueID, data) => {
				await storage.set(inProgressIssueID, data);
				return data;
			},
			updateInProgressIssue: async (inProgressIssueID, data) => {
				if (await storage.get(inProgressIssueID)) {
					await storage.set(inProgressIssueID, data);
				} else {
					fireErrorAnalytics({
						meta: {
							id: 'updateInProgressIssue',
							packageName: 'jiraIssueCreateRestore',
							teamName: 'gryffindor',
						},
						attributes: {
							message:
								"Tried to updateInProgressIssue with an inProgressIssueID that doesn't exist in indexed-db",
						},
						sendToPrivacyUnsafeSplunk: true,
					});
					return data;
				}
				return data;
			},
			deleteInProgressIssue: async (inProgressIssueID) => {
				await storage.remove(inProgressIssueID);
			},
		}),
		[storage],
	);
};
